import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { FileUp, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Reusable CSV/paste importer. `columns` is an ordered array of
// { key, synonyms?: string[] } — columns[0] is the required "name" column.
export const BulkImportDialog = ({
  open, onOpenChange, title, description, columns, sampleCsv, sampleFilename = "import_template.csv",
  onImport, onDone, testid = "bulk",
}) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const parse = (raw) => {
    const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const splitRow = (l) => l.split(/[,\t]/).map((p) => p.trim().replace(/^"|"$/g, ""));
    const header = splitRow(lines[0]).map((h) => h.toLowerCase());
    const keyFor = (h) => {
      for (const c of columns) {
        if (c.key.toLowerCase() === h || (c.synonyms || []).includes(h)) return c.key;
      }
      return null;
    };
    const firstKey = columns[0].key;
    const nameSyn = [firstKey.toLowerCase(), ...(columns[0].synonyms || [])];
    const hasHeader = header.some((h) => nameSyn.includes(h));
    const map = hasHeader ? header.map(keyFor) : [firstKey];
    return (hasHeader ? lines.slice(1) : lines).map((line) => {
      const cells = splitRow(line);
      const row = {};
      cells.forEach((c, i) => { const k = map[i]; if (k && c) row[k] = c; });
      if (!hasHeader && cells[0]) row[firstKey] = cells[0];
      return row[firstKey] ? row : null;
    }).filter(Boolean);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = "";
  };

  const downloadSample = () => {
    const blob = new Blob([sampleCsv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = sampleFilename; a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    const rows = parse(text);
    if (!rows.length) { toast.error("Paste rows or upload a CSV with at least a name column"); return; }
    setBusy(true);
    try {
      const res = await onImport(rows);
      const created = res?.created ?? rows.length;
      const skipped = res?.skipped ? ` · ${res.skipped} skipped` : "";
      toast.success(`Imported ${created} record${created === 1 ? "" : "s"}${skipped}`);
      setText("");
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      // 402 (Pro-gate) is surfaced by the global interceptor toast.
      if (err?.response?.status !== 402) toast.error("Import failed — check your columns and try again.");
    } finally {
      setBusy(false);
    }
  };

  const colHint = columns.map((c) => c.key).join(", ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
          <DialogDescription className="text-zinc-400 text-[12px]">
            {description} Columns: <span className="font-mono text-zinc-300">{colHint}</span>. The first column (name) is required.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onFile} data-testid={`${testid}-file-input`} />
          <button onClick={() => fileRef.current?.click()} data-testid={`${testid}-upload-btn`} className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            <FileUp className="h-3.5 w-3.5" /> Upload CSV file
          </button>
          <button onClick={downloadSample} data-testid={`${testid}-sample-btn`} className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            <Download className="h-3.5 w-3.5" /> Sample CSV
          </button>
        </div>
        <textarea
          data-testid={`${testid}-textarea`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder={sampleCsv}
          className="w-full rounded-md bg-zinc-950 border border-zinc-700 p-3 text-[12px] font-mono text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-600/50"
        />
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="text-[12px] px-3.5 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid={`${testid}-submit`} className="flex items-center gap-1.5 text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Import
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
